import { useCallback, useEffect, useState } from 'react';
import type {
  VersionConfidence,
  VersionFamily,
  VersionFamilyListResponse,
  VersionFamilyMember,
} from '@kennisbank/shared';
import { ApiError, apiGet, apiPost } from '../api/client.js';
import { Icon } from './Icon.js';
import {
  EFFECTIVE_DATE_SOURCE_LABEL,
  formatEffectiveDate,
  similarityLabel,
  statusLabel,
} from '../lib/document-versions.js';

/**
 * "Versions" view on /admin/documents — review and correct document-version
 * families detected at ingest (see agents/knowledge-agent/src/document_versions.py).
 *
 * Tabs: Needs review (uncertain groups — no ranking change until confirmed),
 * Applied automatically (confident groups already ranking older copies
 * lower), Confirmed (admin decisions; never recomputed). Every action is
 * kb_admin-only and audited server-side.
 */

const API = '/api/v1/admin/document-versions';

const TABS: Array<{ key: VersionConfidence; label: string; hint: string }> = [
  {
    key: 'review',
    label: 'Needs review',
    hint: 'Same-name documents the detector was not sure about. Nothing changes in search until you decide.',
  },
  {
    key: 'auto',
    label: 'Applied automatically',
    hint: 'Confident matches. Older copies already rank lower in answers (they are never hidden).',
  },
  {
    key: 'confirmed',
    label: 'Confirmed',
    hint: 'Decisions made by an admin. Re-ingest and re-detection keep them.',
  },
];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

function StatusChip({ member }: { member: VersionFamilyMember }): JSX.Element {
  const label = statusLabel(member);
  const tone =
    label === 'Current'
      ? 'bg-nebula-success-bg text-nebula-success'
      : label === 'Superseded'
        ? 'bg-nebula-gray-5 text-nebula-fg-3'
        : label === 'Needs review'
          ? 'bg-nebula-warning-bg text-nebula-warning'
          : 'bg-nebula-tint text-nebula-secondary';
  return (
    <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {label}
    </span>
  );
}

interface FamilyCardProps {
  family: VersionFamily;
  busy: boolean;
  onAction: (path: string, body: Record<string, unknown>) => Promise<void>;
}

function FamilyCard({ family, busy, onAction }: FamilyCardProps): JSX.Element {
  const base = `${API}/families/${family.familyId}`;
  const reason = family.members.find((m) => m.versionReason)?.versionReason;
  const isReview = family.confidence === 'review';
  return (
    <article className="rounded-lg border border-nebula-border bg-nebula-surface">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-nebula-border-soft px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="folder_copy" size={18} className="text-nebula-secondary" />
            <h3 className="ov-h-card truncate">{family.members.length} copies</h3>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                family.ring === 'ring0'
                  ? 'bg-nebula-gray-5 text-nebula-fg-1'
                  : 'bg-nebula-tint text-nebula-secondary'
              }`}
            >
              {family.ring === 'ring0' ? 'Internal' : 'Product'}
            </span>
          </div>
          {isReview && reason && <p className="ov-meta mt-1 max-w-3xl">{reason}</p>}
        </div>
        <div className="flex flex-shrink-0 gap-2">
          {family.confidence !== 'confirmed' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onAction(`${base}/confirm`, {})}
              className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
              title="Treat these as versions of one document, with the top row as current."
            >
              <Icon name="check" size={16} />
              {isReview ? 'Confirm as versions' : 'Confirm'}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void onAction(`${base}/standalone`, {})}
            className="inline-flex items-center gap-1 rounded-md border border-nebula-border px-3 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50"
            title="These are different documents. Rank each on its own."
          >
            <Icon name="call_split" size={16} />
            Keep separate
          </button>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed text-sm">
          <thead>
            <tr className="ov-meta-upper text-left">
              <th className="w-[38%] px-4 py-2 font-medium">File</th>
              <th className="w-[10%] px-2 py-2 font-medium">Version</th>
              <th className="w-[14%] px-2 py-2 font-medium">Effective date</th>
              <th className="w-[9%] px-2 py-2 font-medium">Overlap</th>
              <th className="w-[11%] px-2 py-2 font-medium">Status</th>
              <th className="w-[18%] px-2 py-2 font-medium" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {family.members.map((m) => (
              <tr key={m.filename} className="border-t border-nebula-border-soft align-top">
                <td className="px-4 py-2">
                  <div className="break-all font-medium text-nebula-fg-1">{m.filename}</div>
                  {!isReview && m.versionReason && (
                    <div className="ov-meta mt-0.5">{m.versionReason}</div>
                  )}
                </td>
                <td className="px-2 py-2 font-numeric text-nebula-fg-2">{m.versionLabel ?? '—'}</td>
                <td
                  className="px-2 py-2 text-nebula-fg-2"
                  title={
                    m.effectiveDateSource
                      ? `From the ${EFFECTIVE_DATE_SOURCE_LABEL[m.effectiveDateSource]}`
                      : undefined
                  }
                >
                  {formatEffectiveDate(m.effectiveDate)}
                </td>
                <td
                  className="px-2 py-2 font-numeric text-nebula-fg-2"
                  title="Share of text this copy has in common with the top row."
                >
                  {similarityLabel(m.versionSimilarity)}
                </td>
                <td className="px-2 py-2">
                  <StatusChip member={m} />
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    {m.versionStatus !== 'current' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void onAction(`${base}/current`, { filename: m.filename })}
                        className="rounded px-2 py-1 text-xs text-nebula-secondary hover:bg-nebula-tint disabled:opacity-50"
                      >
                        Make current
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onAction(`${base}/split`, { filename: m.filename })}
                      className="rounded px-2 py-1 text-xs text-nebula-fg-3 hover:bg-nebula-tint disabled:opacity-50"
                      title="Not a copy of the others — take it out of this group."
                    >
                      Split out
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

export function DocumentVersionsPanel(): JSX.Element {
  const [tab, setTab] = useState<VersionConfidence>('review');
  const [families, setFamilies] = useState<VersionFamily[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (append: string | null): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet<VersionFamilyListResponse>(`${API}/families`, {
          confidence: tab,
          cursor: append ?? undefined,
          limit: 20,
        });
        setFamilies((prev) => (append ? [...prev, ...res.families] : res.families));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setLoading(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const onAction = async (path: string, body: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiPost(path, body);
      await load(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const activeTab = TABS.find((t) => t.key === tab)!;

  return (
    <section className="mb-6" aria-labelledby="doc-versions-heading">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="doc-versions-heading" className="ov-h-section">
            Document versions
          </h2>
          <p className="ov-meta mt-0.5 max-w-3xl">{activeTab.hint}</p>
        </div>
        <div role="tablist" className="inline-flex rounded-md border border-nebula-border p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`rounded px-3 py-1 text-xs font-medium ${
                tab === t.key
                  ? 'bg-nebula-primary text-white'
                  : 'text-nebula-fg-2 hover:bg-nebula-tint'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
          <Icon name="error" size={16} />
          {error}
        </div>
      )}

      <div className="space-y-3">
        {families.map((f) => (
          <FamilyCard key={f.familyId} family={f} busy={busy} onAction={onAction} />
        ))}
      </div>

      {loading && (
        <div className="ov-meta flex items-center gap-2 py-4">
          <Icon name="progress_activity" size={16} className="animate-spin" />
          Loading…
        </div>
      )}
      {!loading && families.length === 0 && !error && (
        <p className="ov-meta rounded-lg border border-dashed border-nebula-border px-4 py-6 text-center">
          {tab === 'review' ? 'Nothing waiting for review.' : 'No families in this list.'}
        </p>
      )}
      {!loading && cursor && (
        <button
          type="button"
          onClick={() => void load(cursor)}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-nebula-border px-3 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint"
        >
          <Icon name="expand_more" size={16} />
          Load more
        </button>
      )}
    </section>
  );
}
